<template>
  <CkEditor
    :editor="editor"
    :config="editorConfig"
    v-model="editorData"
    @ready="onReady"
  />
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import DecoupledEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor'
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials'

import FontPlugin from '@ckeditor/ckeditor5-font/src/font'

import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold'
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic'
import UnderlinePlugin from '@ckeditor/ckeditor5-basic-styles/src/underline'

import AlignmentPlugin from '@ckeditor/ckeditor5-alignment/src/alignment'

import TablePlugin from '@ckeditor/ckeditor5-table/src/table'
import TableToolbarPlugin from '@ckeditor/ckeditor5-table/src/tabletoolbar'

import ImagePlugin from '@ckeditor/ckeditor5-image/src/image'
import ImageToolbarPlugin from '@ckeditor/ckeditor5-image/src/imagetoolbar'
import ImageCaptionPlugin from '@ckeditor/ckeditor5-image/src/imagecaption'
import ImageStylePlugin from '@ckeditor/ckeditor5-image/src/imagestyle'
import ImageResizePlugin from '@ckeditor/ckeditor5-image/src/imageresize'
import LinkImagePlugin from '@ckeditor/ckeditor5-link/src/linkimage'

import ImageBlockPlugin from '@ckeditor/ckeditor5-image/src/imageblock'
import ImageInlinePlugin from '@ckeditor/ckeditor5-image/src/imageinline'

import ImageUploadPlugin from '@ckeditor/ckeditor5-image/src/imageupload'
import UploadS3Plugin from '@/libs/UploadS3Plugin'

import LinkPlugin from '@ckeditor/ckeditor5-link/src/link'
import AutoLinkPlugin from '@ckeditor/ckeditor5-link/src/autolink'
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph'
import ListPlugin from '@ckeditor/ckeditor5-list/src/list'
import IndentPlugin from '@ckeditor/ckeditor5-indent/src/indent'
import IndentBlockPlugin from '@ckeditor/ckeditor5-indent/src/indentblock'

import ClipboardPlugin from '@ckeditor/ckeditor5-clipboard/src/clipboard'
import RemoveFormatPlugin from '@ckeditor/ckeditor5-remove-format/src/removeformat'
import PasteFromOfficePlugin from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice'

export default {
  components: {
    CkEditor: CKEditor.component
  },

  props: {
    value: {},
    targetToolbar: {}
  },

  data () {
    let editorData = forceStr(this.value)
    if (!editorData) editorData = '<div><br><br>---</div>'

    return {
      editor: DecoupledEditor,
      editorData,
      editorInstance: null,

      editorConfig: {
        plugins: [
          FontPlugin,

          BoldPlugin,
          ItalicPlugin,
          UnderlinePlugin,

          AlignmentPlugin,
          ListPlugin,

          IndentPlugin,
          IndentBlockPlugin,

          LinkPlugin,
          AutoLinkPlugin,

          ParagraphPlugin,
          EssentialsPlugin,

          ImagePlugin,
          ImageToolbarPlugin,
          ImageCaptionPlugin,
          ImageStylePlugin,
          ImageResizePlugin,
          LinkImagePlugin,
          ImageBlockPlugin,
          ImageInlinePlugin,

          ImageUploadPlugin,
          UploadS3Plugin,

          TablePlugin,
          TableToolbarPlugin,

          ClipboardPlugin,
          RemoveFormatPlugin,
          PasteFromOfficePlugin
        ],

        toolbar: {
          shouldNotGroupWhenFull: true,
          items: [
            'fontsize',
            'bold',
            'italic',
            'underline',
            'alignment',
            'removeFormat',
            '|',
            'fontcolor',
            'fontBackgroundColor',
            '|',
            'bulletedList',
            'numberedList',
            'outdent',
            'indent',
            '|',
            'link',
            'imageUpload',
            'insertTable'
          ]
        },

        image: {
          toolbar: [
            'imageStyle:inline',
            'imageStyle:block',
            'imageStyle:side',
            '|',
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'resizeImage',
            '|',
            'linkImage'
          ]
        },

        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        }
      }
    }
  },

  methods: {
    onReady (editor) {
      this.editorInstance = editor
      if (this.targetToolbar) {
        if (this.targetToolbar.childNodes.length) this.targetToolbar.removeChild(this.targetToolbar.firstElementChild)
        this.targetToolbar.appendChild(editor.ui.view.toolbar.element)
      }
    },

    setContentPreservingUndo (newContent) {
      if (!this.editorInstance) {
        this.editorData = forceStr(newContent)
        return
      }

      try {
        this.editorInstance.model.change(writer => {
          const root = this.editorInstance.model.document.getRoot()
          writer.remove(writer.createRangeIn(root))
          const viewFragment = this.editorInstance.data.processor.toView(newContent)
          const modelFragment = this.editorInstance.data.toModel(viewFragment)
          writer.insert(modelFragment, root, 0)
        })
      } catch (error) {
        this.editorData = forceStr(newContent)
      }
    }
  },

  watch: {
    editorData (editorData) {
      if (isEqual(editorData, this.value)) return
      this.$emit('input', forceStr(editorData))
    },

    value (value) {
      if (isEqual(this.editorData, value)) return
      this.editorData = forceStr(value)
    }
  }
}

const forceStr = (val) => String(val || '')
const isEqual = (source, target) => forceStr(source) === forceStr(target)
</script>

<style lang="sass">
  .ck-editor__editable_inline
    height: 100%

  .ck.ck-content.ck-editor__editable.ck-editor__editable_inline.ck-focused
    border: none
    box-shadow: none

  .ck.ck-toolbar
    .ck.ck-toolbar__items
      font-size: 0.8em
</style>
